import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useState } from 'react'
import { FlatList, Image } from 'react-native'
import { TouchableOpacity } from 'react-native-gesture-handler'
import { SafeAreaView } from 'react-native-safe-area-context'

import { useFocusEffect, useRouter } from 'expo-router'

import {
  DailyInspirationCommentSheet,
  InspirationSheetType
} from '../../components/dailyInspirationCommentSheet/DailyInspirationCommentSheet'
import { useDailyInspirationCommentSheet } from '../../components/dailyInspirationCommentSheet/useDailyInspirationCommentSheet'
import { ReportResponseSheet, useReportResponseSheet } from '../../components/ReportResponseSheet'
import { DS } from '../../design-system'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { color } from '../../theme'
import { extendLogger, LoggerContexts } from '../../utils/logger'
import DailyInspirationCard from './DailyInspirationCard'
import { Header } from './Header'
import { OtherResponseCard, UserResponseCard } from './ResponseCard'
import { styles } from './styles'

import { useRemoteConfig } from '../../../module/remote-config/useRemoteConfig'
import { WebWrapper } from '../../components/WebWrapper'
const OTHER_USER_BADGE_COLOR = [color.palette.orange, color.palette.blue, color.palette.purple]
const logger = extendLogger(LoggerContexts.SessionDaily)

export const DiscussionScreen = observer(() => {
  const router = useRouter()
  const {
    session: {
      user,
      firstName,
      dailyInspirationalQuoteData,
      getDailyInspirationResponses,
      allInspirationResponses,
      deleteDailyInspirationResponse,
      updateDailyInspirationResponses,
      updateInspirationResponsesEmojis,
      addInspirationResponsesReport,
      pageNumber,
      setPageNumber,
      hasMore,
      addDailyInspirationResponses,
      getInspoReactionsUsingId,
      setHasMore,
      getUserResponseCount
    },
    analytics: { setEvent }
  } = useStore()

  const { openDailyInspirationCommentSheet, dailyInspirationCommentSheetRef, closeDailyInspirationCommentSheet } =
    useDailyInspirationCommentSheet()
  const { reportResponseRef, openReportResponseSheet, closeReportResponseSheet } = useReportResponseSheet()
  logger.info(allInspirationResponses.length)
  const userInitial = firstName[0]
  const userId = user?._id.toString()
  const [isEmojiListVisible, setIsEmojiListVisible] = useState(false)
  const [contentType, setContentType] = useState(InspirationSheetType.DEFAULT)
  const [editResponseDataId, setEditResponseDataId] = useState('')
  const [editResponseDataMsg, setEditResponseDataMsg] = useState('')
  const [selectedIdForImojiSelection, setSelectedIdForImojiSelection] = useState('')
  const [isReportSubmitted, setIsReportSubmitted] = useState(false)
  const [reportResponseId, setReportResponseId] = useState('')
  const [isLoadingMore, setIsLoadingMore] = useState(false) // Loading more items state
  const [isReponseLoading, setIsReponseLoading] = useState(false)
  const [autherName, setAutherName] = useState('')
  const [content, setContent] = useState('')
  const [loader, setLoader] = useState(false)
  const isDailyDiscussionsEnabled = useRemoteConfig((state) => state.is_daily_discussions_enabled)

  const fetchDailyInspirationResponses = async () => {
    await getDailyInspirationResponses()
  }

  useFocusEffect(
    useCallback(() => {
      if (pageNumber === 0) {
        setLoader(true)
        fetchDailyInspirationResponses()
      }
      setLoader(false)
    }, [pageNumber])
  )

  const openReportSheet = (reportId: string, author: string, response: string) => {
    setAutherName(author)
    setContent(response)
    setReportResponseId(reportId)
    setTimeout(() => {
      openReportResponseSheet()
    }, 100)
  }

  const addNewResponse = () => {
    setEditResponseDataMsg('')
    setEditResponseDataId('')
    setContentType(InspirationSheetType.DEFAULT)
    setTimeout(() => {
      openDailyInspirationCommentSheet()
    }, 100)
  }

  const handleEdit = () => {
    setContentType(InspirationSheetType.EDIT)
    setTimeout(() => {
      openDailyInspirationCommentSheet()
    }, 100)
  }

  const goToHome = () => {
    setPageNumber(0)
    setHasMore(true)
    router.replace('/(root)/(tabs)')
  }

  const handleReaction = async (emoji: string, responseId: string, response: string, authorName: string) => {
    setIsEmojiListVisible(false)
    await updateInspirationResponsesEmojis(emoji, responseId)
    setEvent(events.dailyInspirationEvents.response_engaged, {
      emoji,
      content: response,
      author: authorName
    })
  }

  const saveEditResponse = async (responseMsg: string, responseId: string) => {
    await updateDailyInspirationResponses(responseMsg, responseId)
  }

  const onDelete = async (responseId: string) => {
    await deleteDailyInspirationResponse(responseId)
    await getUserResponseCount()
  }

  const handleReport = async (reason: string, responseId: string) => {
    await addInspirationResponsesReport(reason, responseId)
    setEvent(events.dailyInspirationEvents.response_reported, {
      reason: reason,
      content: content,
      author: autherName
    })
    setIsReportSubmitted(true)
  }

  const fetchData = async (pageNum: number) => {
    if (isLoadingMore || !hasMore) return
    setIsLoadingMore(pageNum > 0)
    try {
      setPageNumber(pageNum + 1)
      await getDailyInspirationResponses()
    } catch (error) {
      logger.error('Error fetching data:', error)
    } finally {
      setIsLoadingMore(false)
    }
  }

  const submitNewResponse = async (response: string) => {
    setIsReponseLoading(true)
    await addDailyInspirationResponses(response)
    await getInspoReactionsUsingId()
    await getUserResponseCount()
    setIsReponseLoading(false)
    closeDailyInspirationCommentSheet()
  }

  return (
    <SafeAreaView style={styles.container}>
      <WebWrapper>
        <StatusBar animated={true} style={'dark' as StatusBarStyle} hidden={false} />
        <Header
          isDailyDiscussionsEnabled={isDailyDiscussionsEnabled}
          onBackPress={goToHome}
          addNewResponse={addNewResponse}
        />
        <FlatList
          bounces={false}
          showsVerticalScrollIndicator={false}
          data={allInspirationResponses}
          ListHeaderComponent={() => {
            return (
              <DS.Frame style={{ marginBottom: 25 }}>
                <DailyInspirationCard />
                {allInspirationResponses.length === 0 && loader && (
                  <DS.Frame style={{ marginTop: 30 }}>
                    <Image
                      source={require('./assets/spinner.gif')}
                      resizeMode="contain"
                      style={{ alignSelf: 'center', width: 42, height: 42 }}
                    />
                  </DS.Frame>
                )}
              </DS.Frame>
            )
          }}
          keyExtractor={(item) => item?._id?.toString() || Math.random().toString()}
          extraData={allInspirationResponses}
          onEndReached={() => {
            if (!isLoadingMore) {
              fetchData(pageNumber)
            }
          }}
          onEndReachedThreshold={0.5}
          ListFooterComponent={
            isLoadingMore ? (
              <Image
                source={require('./assets/spinner.gif')}
                resizeMode="contain"
                style={{ alignSelf: 'center', width: 42, height: 42, marginVertical: 5 }}
              />
            ) : null
          }
          renderItem={({ item, index }) => {
            if (item.userId === userId) {
              return (
                <UserResponseCard
                  key={item._id}
                  responseId={item._id}
                  userName={item.userName}
                  response={item.response}
                  handleEdit={handleEdit}
                  setEditResponseDataId={setEditResponseDataId}
                  setEditResponseDataMsg={setEditResponseDataMsg}
                />
              )
            }
            return (
              <React.Fragment key={'other-response-list'}>
                {
                  //NOTE - Add extra space between my response list and other response list
                  index > 0 &&
                    allInspirationResponses[index - 1].userId === userId &&
                    allInspirationResponses[index].userId !== userId && <DS.Frame style={{ height: 24 }} />
                }
                <OtherResponseCard
                  key={item._id}
                  responseId={item._id}
                  userName={item.userName}
                  response={item.response}
                  emojis={item.emojis}
                  isEmojiListVisible={isEmojiListVisible}
                  setIsEmojiListVisible={setIsEmojiListVisible}
                  handleReaction={handleReaction}
                  onPressReport={openReportSheet}
                  selectedIdForImojiSelection={selectedIdForImojiSelection}
                  setSelectedIdForImojiSelection={setSelectedIdForImojiSelection}
                  setReportResponseId={setReportResponseId}
                  userBadgeColor={OTHER_USER_BADGE_COLOR[index % OTHER_USER_BADGE_COLOR.length]}
                />
              </React.Fragment>
            )
          }}
          style={{ marginTop: 0 }}
        />
        <DailyInspirationCommentSheet
          inspirationMessage={dailyInspirationalQuoteData?.acf?.motivational_message ?? ''}
          userInitial={userInitial}
          onClose={closeDailyInspirationCommentSheet}
          ref={dailyInspirationCommentSheetRef}
          onSubmit={submitNewResponse}
          saveEditResponse={saveEditResponse}
          type={contentType}
          editResponseDataId={editResponseDataId}
          editResponseDataMsg={editResponseDataMsg}
          onDelete={onDelete}
          isReponseLoading={isReponseLoading}
        />
        <ReportResponseSheet
          ref={reportResponseRef}
          onClose={() => {
            setIsReportSubmitted(false)
            closeReportResponseSheet()
          }}
          onReport={handleReport}
          reportResponseId={reportResponseId}
          isReportSubmitted={isReportSubmitted}
          setIsReportSubmitted={setIsReportSubmitted}
        />
      </WebWrapper>
    </SafeAreaView>
  )
})
